import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { TierBadge } from './TierBadge'
import { ArticleReaderPane } from './ArticleReaderPane'
import { useStorySources } from '../../hooks/stories'
import { useConfirmedExternalLink } from './OffSiteLinkConfirmModal'

function formatPublishedDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toISOString().slice(0, 10)
}

/**
 * Inline sources section on the Story Detail page.
 *
 * Promotes the per-article reader (previously buried 3 clicks deep inside
 * `SourcesModal`) to a primary CTA on the Story Detail page itself. Each row
 * exposes:
 *   - Source title + publisher + tier + canonical badge + published date
 *   - "Read on IntelHub" button (when `fullTextFetchedAt` is non-null) that
 *     toggles an inline `ArticleReaderPane`
 *   - "Open in source" external-link button (always visible) that runs through
 *     the off-site confirmation modal
 *
 * Behaviour parity with `SourcesModal`:
 *   - `fullTextFetchedAt` null  → reader button hidden (pre-feature article)
 *   - `fullTextFetchedAt` set, `fullText` null → button shows; expanded pane
 *     renders the "Content not retrievable" message
 *   - One reader open at a time; opening another collapses the previous (matches
 *     the modal's `readerArticleId` single-state pattern)
 *
 * The full-source aggregator stays accessible via a "View all N sources" CTA
 * that opens `SourcesModal` — caller passes `onOpenFullSourcesModal`.
 */
export function StoryDetailSourcesSection({
  storyId,
  totalSourceCount,
  onOpenFullSourcesModal,
}: {
  storyId: string
  totalSourceCount: number
  onOpenFullSourcesModal: () => void
}) {
  const { data, isLoading, error } = useStorySources(storyId)
  const { confirmAndOpen, modal } = useConfirmedExternalLink()
  const [readerArticleId, setReaderArticleId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <p className="py-4 text-sm text-muted-foreground">Loading sources…</p>
    )
  }
  if (error) {
    return (
      <p className="py-4 text-sm text-rose-600">Failed to load sources.</p>
    )
  }
  if (!data) return null

  return (
    <section
      aria-label="Sources"
      data-testid="story-detail-sources-section"
      className="space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Sources
        </h2>
        {totalSourceCount > data.sources.length && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={onOpenFullSourcesModal}
          >
            View all {totalSourceCount} sources
          </Button>
        )}
      </div>

      <ul className="divide-y rounded-md border">
        {data.sources.map((s) => {
          const isReaderOpen = readerArticleId === s.articleId
          // KAN-48 — CTA visibility and label are status-aware.
          // - Pre-feature rows (no status + no fetched_at) hide the CTA.
          // - Available rows show "Read on IntelHub".
          // - Skipped/unavailable/failed rows show "Full text unavailable" so
          //   the reader knows up-front that clicking will not return text.
          // - Pre-feature rows where the legacy pipeline left fetched_at set
          //   keep the legacy "Read on IntelHub" affordance so historical
          //   rendering stays exact.
          const status = s.fullTextStatus ?? null
          const readerCtaVisible =
            status != null ? true : s.fullTextFetchedAt !== null
          const closedLabel =
            status != null && status !== 'available'
              ? 'Full text unavailable'
              : 'Read on IntelHub'

          return (
            <li key={s.articleId} className="p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{s.publisher}</span>
                <TierBadge tier={s.sourceTier} />
                {s.isCanonical && <Badge variant="secondary">Canonical</Badge>}
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatPublishedDate(s.publishedAt)}
                </span>
              </div>

              <p className="mt-1 text-sm text-foreground">{s.title}</p>

              <div className="mt-2 flex flex-wrap gap-2">
                {readerCtaVisible && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setReaderArticleId(isReaderOpen ? null : s.articleId)
                    }
                    aria-expanded={isReaderOpen}
                    aria-controls={`reader-${s.articleId}`}
                  >
                    {isReaderOpen ? 'Close reader' : closedLabel}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => confirmAndOpen(s.url)}
                >
                  Open in source
                  <ExternalLink className="ml-1.5 h-3 w-3" aria-hidden />
                </Button>
              </div>

              {isReaderOpen && (
                <div id={`reader-${s.articleId}`} className="mt-3">
                  <ArticleReaderPane
                    fullText={s.fullText}
                    fullTextMeta={s.fullTextMeta ?? null}
                    fullTextStatus={s.fullTextStatus ?? null}
                    fullTextReason={s.fullTextReason ?? null}
                    publisher={s.publisher}
                    url={s.url}
                    onContinueToSource={confirmAndOpen}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {modal}
    </section>
  )
}
